import { Component, type ErrorInfo, type ReactNode } from 'react'

import { WORTLAUT } from '../texte'

/**
 * Was zu sehen ist, wenn beim Zeichnen etwas wirft.
 *
 * **Der Anlass war ein weißer Bildschirm**, und der ist das Schlimmste, was
 * diese Anwendung anzeigen kann: aus dem Betrieb kam „kurze Anzeige: wird
 * geladen, dann nur ein weißer Bildschirm" — am iPhone in der Web-App, in
 * Safari und am iPad nicht. Aus einem weißen Bildschirm lässt sich **nichts**
 * ableiten: nicht, was schiefging, nicht, in welchem Stand, nicht, ob es am
 * Gerät liegt oder am Bau. Es kostete eine ganze Runde Raten.
 *
 * Der Grund, warum er überhaupt möglich war: **es gab keine einzige
 * Fehlergrenze im ganzen Baum.** React hängt bei einem Fehler im Zeichnen den
 * gesamten Baum ab — ohne eine solche Grenze bleibt das leere Wurzelelement.
 *
 * Das ist dieselbe Regel wie überall hier: **keine stille Absage.** Etwas geht
 * schief und nichts passiert — das gab es bei den Reaktionen einmal und soll
 * es nie wieder geben. Ein weißer Bildschirm ist ihre schlimmste Form.
 *
 * **Was hier steht, ist für den Menschen davor und für die Fehlersuche
 * zugleich:** der Satz, der Stand der Oberfläche und die Meldung im Klartext.
 * Ein Bildschirmfoto davon sagt alles, was ein weißer Bildschirm verschweigt.
 */
interface Lage {
  fehler: Error | null
}

export class Absturz extends Component<{ children: ReactNode }, Lage> {
  state: Lage = { fehler: null }

  static getDerivedStateFromError(fehler: Error): Lage {
    return { fehler }
  }

  componentDidCatch(fehler: Error, wo: ErrorInfo): void {
    // In die Konsole, für den Fall, dass jemand mit einem Mac danebensitzt.
    // Der Bildschirm zeigt die Meldung ohnehin.
    console.error('[travel-broadcast] Absturz beim Zeichnen', fehler, wo)
  }

  render(): ReactNode {
    const { fehler } = this.state
    if (fehler === null) return this.props.children

    const t = WORTLAUT.absturz
    return (
      <div className="absturz" role="alert">
        <h1 className="absturz__titel">{t.titel}</h1>
        <p className="absturz__lead">{t.lead}</p>
        <p className="absturz__stand">{t.stand(__BUILD__)}</p>
        {/*
          **Die Meldung steht als Text da, nicht als `title`.** Ein Tooltip ist
          auf dem Telefon unsichtbar — und genau dort ist der Bildschirm weiß
          geworden.
        */}
        <pre className="absturz__meldung">{fehler.message || String(fehler)}</pre>
        <button className="button" type="button" onClick={() => void neuAnfangen()}>
          {t.neuLaden}
        </button>
        <p className="absturz__hinweis">{t.hinweis}</p>
      </div>
    )
  }
}

/**
 * Der Notausgang: **Zwischenspeicher weg, Service Worker abmelden, neu laden.**
 *
 * Der häufigste Grund für einen Bildschirm, der auf einem Gerät weiß bleibt
 * und auf dem daneben nicht, ist ein Zwischenspeicher, der nicht mehr zu dem
 * passt, was auf dem Server liegt — und eine Web-App vom Home-Bildschirm hat
 * ihren **eigenen**, getrennt von Safari. Deshalb hilft dort das Übliche
 * nicht: Seite neu laden holt dieselbe Hülle noch einmal.
 *
 * **Was hier wegkommt, ist nur Zwischengespeichertes.** Der Ausgangskorb liegt
 * in IndexedDB und wird nicht angefasst: was jemand ohne Netz geschrieben hat,
 * darf ein Aufräumen nicht wegwerfen.
 */
async function neuAnfangen(): Promise<void> {
  try {
    if ('caches' in window) {
      const namen = await caches.keys()
      await Promise.all(namen.map((n) => caches.delete(n)))
    }
  } catch {
    // Ein Aufräumen, das scheitert, darf das Neuladen nicht verhindern.
  }
  try {
    if ('serviceWorker' in navigator) {
      const alle = await navigator.serviceWorker.getRegistrations()
      await Promise.all(alle.map((r) => r.unregister()))
    }
  } catch {
    // dito
  }
  window.location.reload()
}
